import {MechanicValidator} from './MechanicValidator'
import {CardValidatorOrdered, PlayerValidator} from '@colonist/game-mechanic-validators'
import {ActionStatus, PieceTypes} from '@colonist/game-base-types'
import {MapValidator} from '../core/map/MapValidator'
import {getCardsForPiece} from '@colonist/game-card-types'
import {PlayerColor, MechanicDevelopmentCardsState, CardEnum, NestedReadonly} from '@colonist/game-states'
import {ArrayUtils} from '@colonist/utils'
import {MechanicValidatorDevelopmentCardPlayer} from './MechanicValidatorDevelopmentCardPlayer'

export class MechanicValidatorDevelopmentCard extends MechanicValidator {
    private readonly bankDevelopmentCards: CardValidatorOrdered
    private readonly playerDevelopmentCardStates: Map<PlayerColor, MechanicValidatorDevelopmentCardPlayer>

    constructor(playerStates: PlayerValidator[], mapState: MapValidator, developmentCardsState: NestedReadonly<MechanicDevelopmentCardsState>) {
        super(playerStates, mapState)

        this.bankDevelopmentCards = new CardValidatorOrdered(developmentCardsState.bankDevelopmentCards)

        this.playerDevelopmentCardStates = new Map()
        for(const player in developmentCardsState.players) {
            const playerColor = Number(player) as PlayerColor
            const playerDevelopmentCardState = developmentCardsState.players[playerColor]

            if(playerDevelopmentCardState == undefined) continue

            this.playerDevelopmentCardStates.set(playerColor, new MechanicValidatorDevelopmentCardPlayer(playerDevelopmentCardState))
        }
        this.playerDevelopmentCardStates.set(PlayerColor.None, new MechanicValidatorDevelopmentCardPlayer({
            developmentCards: {cards: []},
            developmentCardsUsed: [],
        }))
    }

    getDevelopmentCardsPlayerState(player: PlayerColor): MechanicValidatorDevelopmentCardPlayer {
        const devCardsPlayerState = this.playerDevelopmentCardStates.get(player)
        if(devCardsPlayerState === undefined) throw new Error(`No MechanicDevelopmentCardsPlayerState for player ${player}`)
        return devCardsPlayerState
    }

    hasResourcesForDevelopmentCard(player: PlayerColor): ActionStatus {
        const playerController = this.playerValidators.get(player)
        if(playerController == undefined) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noPlayer'}}

        const resourcesForDevelopmentCard = getCardsForPiece(PieceTypes.DevelopmentCard)
        const hasResourcesForDevelopmentCard = playerController.hasResourceCardsInHand(resourcesForDevelopmentCard)
        if(!hasResourcesForDevelopmentCard) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    canPlayerUseDevelopmentCard(player: PlayerColor, card: CardEnum): ActionStatus {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player)
        if(devCardsPlayerState.hasUsedDevelopmentCardThisTurn) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.gameState.gameLogs.canOnlyPlayOneDevCardPerTurn'}}

        const developmentCards = new CardValidatorOrdered(devCardsPlayerState.developmentCards)
        const currentCardCount = developmentCards.getCardCountForCard(card)
        const amountPurchasedThisTurn = ArrayUtils.countOccurenceOfValueInArray(devCardsPlayerState.developmentCardsBoughtThisTurn ?? [], card)
        if(currentCardCount <= amountPurchasedThisTurn) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.gameState.gameLogs.cannotPlayDevCardOnTurnYouBoughtIt'}}

        return {canTakeAction: true}
    }

    canPlayerBuyDevelopmentCard(player: PlayerColor): ActionStatus {
        const isDevelopmentCardAvailableInBank = this.bankDevelopmentCards.cards.length > 0
        if(!isDevelopmentCardAvailableInBank) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.noDevelopmentCardsInBank'}}

        const hasResourcesForDevelopmentCard = this.hasResourcesForDevelopmentCard(player).canTakeAction
        if(!hasResourcesForDevelopmentCard) return {canTakeAction: false, errorMessageIfCantTakeAction: {key: 'strings:game.errors.notEnoughResources'}}

        return {canTakeAction: true}
    }

    canPlayerUseAtLeastOneDevelopmentCard(player: PlayerColor): boolean {
        for(const card of this.getDevelopmentCardsPlayerState(player).developmentCards.cards) {
            if(card == CardEnum.VictoryPoint) continue
            if(!this.canPlayerUseDevelopmentCard(player, card).canTakeAction) continue
            return true
        }
        return false
    }

    getPlayersTotalDevelopmentCards(player: PlayerColor): number {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player)
        return devCardsPlayerState.developmentCards.cards.length
    }

    getPlayersTotalDevelopmentCardsOfType(player: PlayerColor, cardEnum: CardEnum): number {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player)
        const developmentCards = new CardValidatorOrdered(devCardsPlayerState.developmentCards)
        return ArrayUtils.countOccurenceOfValueInArray(developmentCards.cards, cardEnum)
    }

    getPlayersTotalUsedDevelopmentCardsOfType(player: PlayerColor, cardEnum: CardEnum) {
        const devCardsPlayerState = this.getDevelopmentCardsPlayerState(player)
        return ArrayUtils.countOccurenceOfValueInArray(devCardsPlayerState.developmentCardsUsed, cardEnum)
    }

    getTotalDevelopmentCardsInBank(): number {
        return this.bankDevelopmentCards.getNumberOfCardsInInventory()
    }
}
